/**
 * Composes all data needed for LBS/Takt schedule views.
 * Groups section-locations into flowlines by procurement package or work section.
 */

import { useMemo } from "react";
import { useLocations } from "./useLocations";
import { useSections, type Section } from "./useSections";
import { useProcurementPackages, type ProcurementPackage } from "./useProcurementPackages";
import { useSectionLocations, type SectionLocationItem } from "./useSectionLocations";
import {
  buildLocationRows,
  getFlowlineColor,
  parseDate,
  type Flowline,
  type FlowlinePoint,
  type LocationRow,
} from "@/components/schedule/chart-utils";

export type GroupBy = "procurementPackage" | "section";

interface ScheduleData {
  locationRows: LocationRow[];
  flowlines: Flowline[];
  dateRange: { start: Date; end: Date };
  loading: boolean;
}

export function useScheduleData(
  projectId: string | null,
  groupBy: GroupBy,
): ScheduleData {
  const { locations, loading: locLoading } = useLocations(projectId);
  const { sections, loading: secLoading } = useSections();
  const { procurementPackages, loading: ppLoading } = useProcurementPackages();
  const { sectionLocations, loading: slLoading } = useSectionLocations();

  const loading = locLoading || secLoading || ppLoading || slLoading;

  const projectLocations = useMemo(() => {
    if (!projectId) return locations;
    return locations.filter((l) => l.projectId === projectId);
  }, [locations, projectId]);

  const locationRows = useMemo(
    () => buildLocationRows(projectLocations),
    [projectLocations],
  );

  const sectionMap = useMemo(() => {
    const m = new Map<string, Section>();
    for (const s of sections) m.set(s.id, s);
    return m;
  }, [sections]);

  const ppMap = useMemo(() => {
    const m = new Map<string, ProcurementPackage>();
    for (const p of procurementPackages) m.set(p.id, p);
    return m;
  }, [procurementPackages]);

  const locationIdSet = useMemo(
    () => new Set(projectLocations.map((l) => l.id)),
    [projectLocations],
  );

  const relevantSLs = useMemo(
    () => sectionLocations.filter((sl) => locationIdSet.has(sl.locationId)),
    [sectionLocations, locationIdSet],
  );

  const locationOrder = useMemo(() => {
    const m = new Map<string, number>();
    locationRows.forEach((r, i) => m.set(r.id, i));
    return m;
  }, [locationRows]);

  const flowlines = useMemo(() => {
    if (groupBy === "section") {
      return buildSectionFlowlines(relevantSLs, sectionMap, locationOrder);
    } else {
      return buildPPFlowlines(relevantSLs, sectionMap, ppMap, locationOrder);
    }
  }, [groupBy, relevantSLs, sectionMap, ppMap, locationOrder]);

  const dateRange = useMemo(() => {
    const allDates: Date[] = [];
    for (const fl of flowlines) {
      for (const pt of fl.points) {
        allDates.push(pt.startDate, pt.endDate);
      }
    }
    if (allDates.length === 0) {
      const today = new Date();
      const end = new Date(today);
      end.setMonth(end.getMonth() + 3);
      return { start: today, end };
    }
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    allDates.push(today);
    const min = new Date(Math.min(...allDates.map((d) => d.getTime())));
    const max = new Date(Math.max(...allDates.map((d) => d.getTime())));
    min.setDate(min.getDate() - 3);
    max.setDate(max.getDate() + 14);
    return { start: min, end: max };
  }, [flowlines]);

  return { locationRows, flowlines, dateRange, loading };
}

function buildSectionFlowlines(
  sls: SectionLocationItem[],
  sectionMap: Map<string, Section>,
  locationOrder: Map<string, number>,
): Flowline[] {
  // Group by section NAME (not UUID) so same-named sections merge into one flowline
  const bySectionName = new Map<string, SectionLocationItem[]>();
  for (const sl of sls) {
    const section = sectionMap.get(sl.sectionId);
    if (!section) continue;
    const key = section.name;
    if (!bySectionName.has(key)) bySectionName.set(key, []);
    bySectionName.get(key)!.push(sl);
  }

  const flowlines: Flowline[] = [];
  let colorIndex = 0;

  for (const [name, items] of bySectionName) {
    const points = items
      .map((sl) => toFlowlinePoint(sl, locationOrder))
      .filter((p): p is FlowlinePoint & { order: number } => p !== null);

    const merged = deduplicatePoints(points);
    if (merged.length === 0) continue;

    flowlines.push({
      id: name,
      label: name,
      color: getFlowlineColor(colorIndex++),
      points: merged,
    });
  }

  return flowlines;
}

function buildPPFlowlines(
  sls: SectionLocationItem[],
  sectionMap: Map<string, Section>,
  ppMap: Map<string, ProcurementPackage>,
  locationOrder: Map<string, number>,
): Flowline[] {
  // Group by PP name/code (not UUID) so same-named PPs across plots merge
  const byPPName = new Map<string, SectionLocationItem[]>();
  for (const sl of sls) {
    const section = sectionMap.get(sl.sectionId);
    if (!section?.procurementPackageId) continue;
    const pp = ppMap.get(section.procurementPackageId);
    if (!pp) continue;
    const key = pp.name || pp.code;
    if (!byPPName.has(key)) byPPName.set(key, []);
    byPPName.get(key)!.push(sl);
  }

  const flowlines: Flowline[] = [];
  let colorIndex = 0;

  for (const [name, items] of byPPName) {
    const points = items
      .map((sl) => toFlowlinePoint(sl, locationOrder))
      .filter((p): p is FlowlinePoint & { order: number } => p !== null);

    const merged = deduplicatePoints(points);
    if (merged.length === 0) continue;

    flowlines.push({
      id: name,
      label: name,
      color: getFlowlineColor(colorIndex++),
      points: merged,
    });
  }

  return flowlines;
}

function toFlowlinePoint(
  sl: SectionLocationItem,
  locationOrder: Map<string, number>,
): (FlowlinePoint & { order: number }) | null {
  const order = locationOrder.get(sl.locationId);
  if (order === undefined) return null;

  const start = parseDate(sl.plannedStart);
  const end = parseDate(sl.plannedEnd);
  if (!start) return null;

  return {
    locationId: sl.locationId,
    startDate: start,
    endDate: end || start,
    order,
  };
}

/**
 * Merge multiple points at the same location into one per location.
 * Takes the earliest start and latest end.
 */
function deduplicatePoints(
  points: (FlowlinePoint & { order: number })[],
): (FlowlinePoint & { order: number })[] {
  const byLocation = new Map<string, (FlowlinePoint & { order: number })[]>();
  for (const pt of points) {
    if (!byLocation.has(pt.locationId)) byLocation.set(pt.locationId, []);
    byLocation.get(pt.locationId)!.push(pt);
  }

  const merged: (FlowlinePoint & { order: number })[] = [];
  for (const [locationId, pts] of byLocation) {
    const earliest = new Date(Math.min(...pts.map((p) => p.startDate.getTime())));
    const latest = new Date(Math.max(...pts.map((p) => p.endDate.getTime())));
    merged.push({
      locationId,
      startDate: earliest,
      endDate: latest,
      order: pts[0].order,
    });
  }

  return merged.sort((a, b) => a.order - b.order);
}
